import {
  DestroyRef,
  inject,
  Injectable,
  InjectionToken,
  signal,
} from '@angular/core';
import {
  EMPTY,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  timeout,
} from 'rxjs';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { OauthService } from '@tiltgroup/web-components';
import { environment } from '@environments/environment';
import { EmbedSecurityStorage } from './embed-security-storage';
import {
  EmbedAccessMessage,
  EmbedMessageType,
  EmbedTokenMessage,
  isAllowedParentOrigin,
  isEmbedTokenMessage,
  jwtClaims,
} from './embed-protocol';

export interface EmbedParent {
  /** The framing window; the page's own window when it isn't framed. */
  frame: Window;
  /** Read from the browser, not from what the parent says about itself. */
  origin: string | null;
}

export const EMBED_PARENT = new InjectionToken<EmbedParent>('EMBED_PARENT', {
  providedIn: 'root',
  factory: () => ({
    frame: window.parent,
    origin:
      window.location.ancestorOrigins?.[0] ??
      (document.referrer ? new URL(document.referrer).origin : null),
  }),
});

export const EMBED_PARENT_ORIGINS = new InjectionToken<readonly string[]>(
  'EMBED_PARENT_ORIGINS',
  { providedIn: 'root', factory: () => environment.embed.parentOrigins }
);

/** A fresh token is asked for this long before the current one expires. */
export const EMBED_TOKEN_REFRESH_LEAD_MS = 60_000;
/** Asked again this often while the parent hasn't answered. */
export const EMBED_TOKEN_RETRY_MS = 15_000;
/** How long a request the API refused waits for a token before it fails. */
export const EMBED_TOKEN_REFRESH_TIMEOUT_MS = 10_000;

export type EmbedAuthStatus = 'waiting' | 'signedIn' | 'signedOut' | 'refused';

const SIGNED_OUT: LoginResponse = {
  isAuthenticated: false,
  userData: null,
  accessToken: '',
  idToken: '',
};

/**
 * Signs an /embed/ page in with tokens from the page framing it, instead of the login
 * redirect, which can't run in a frame. Only the allowed parent origin is listened to.
 */
@Injectable({ providedIn: 'root' })
export class EmbedAuthService {
  private readonly _oidc = inject(OidcSecurityService);
  private readonly _oauth = inject(OauthService);
  private readonly _storage = inject(EmbedSecurityStorage);
  private readonly _parent = inject(EMBED_PARENT);
  private readonly _allowedOrigins = inject(EMBED_PARENT_ORIGINS);
  private readonly _destroyRef = inject(DestroyRef);

  readonly status = signal<EmbedAuthStatus>('waiting');
  private _started = false;
  private _parentOrigin = '';
  private _configId = '';
  private _timer: ReturnType<typeof setTimeout> | null = null;
  /** The access last told to the parent, as "allowed|reason". */
  private _reportedAccess: string | null = null;
  /** Each access token taken, once stored. */
  private readonly _tokens = new Subject<string>();

  start(): void {
    if (this._started) {
      return;
    }
    this._started = true;

    const { frame, origin } = this._parent;
    if (
      frame === window ||
      !origin ||
      !isAllowedParentOrigin(origin, this._allowedOrigins)
    ) {
      this.status.set('refused');
      return;
    }
    this._parentOrigin = origin;

    const listener = (event: MessageEvent): void => this._onMessage(event);
    window.addEventListener('message', listener);
    this._destroyRef.onDestroy(() => {
      window.removeEventListener('message', listener);
      this._clearTimer();
    });

    this._post({ type: EmbedMessageType.Ready });
  }

  /**
   * Tells the parent whether the user may use the assistant, when that changes, e.g. an
   * admin turns the user off or the allowance runs out, so it can hide or show its tab.
   */
  reportAccess(allowed: boolean, reason?: string | null): void {
    if (this.status() !== 'signedIn') {
      return;
    }

    const message: EmbedAccessMessage = {
      type: EmbedMessageType.Access,
      allowed,
      ...(!allowed && reason ? { reason } : {}),
    };
    const key = `${allowed}|${message.reason ?? ''}`;
    if (key === this._reportedAccess) {
      return;
    }
    this._reportedAccess = key;
    this._post(message);
  }

  /**
   * In place of the OIDC client's refresh, for a request the API refused: the next
   * token from the parent, or signed out when none comes.
   */
  refreshToken(): Observable<LoginResponse> {
    if (this.status() === 'refused') {
      return of(SIGNED_OUT);
    }

    this._post({ type: EmbedMessageType.TokenRequest });
    return this._tokens.pipe(
      take(1),
      map(
        (accessToken): LoginResponse => ({
          ...SIGNED_OUT,
          isAuthenticated: true,
          accessToken,
          configId: this._configId,
        })
      ),
      timeout({
        first: EMBED_TOKEN_REFRESH_TIMEOUT_MS,
        with: () => of(SIGNED_OUT),
      })
    );
  }

  /**
   * In place of the login redirect: the parent's tokens no longer work. A token it
   * sends later, e.g. after signing in again, signs the page back in.
   */
  signOut(): void {
    // Signed in again, the access is told afresh.
    this._reportedAccess = null;
    if (this.status() !== 'refused') {
      this.status.set('signedOut');
    }
  }

  private _onMessage(event: MessageEvent): void {
    if (
      event.source !== this._parent.frame ||
      event.origin !== this._parentOrigin ||
      !isEmbedTokenMessage(event.data)
    ) {
      return;
    }

    const expiresAt = expiryOf(event.data.accessToken);
    const userData = jwtClaims(event.data.idToken);
    if (!expiresAt || expiresAt <= Date.now() || !userData) {
      return;
    }

    this._signIn(event.data, expiresAt, userData);
    this._requestTokenIn(expiresAt - Date.now() - EMBED_TOKEN_REFRESH_LEAD_MS);
  }

  private _signIn(
    message: EmbedTokenMessage,
    expiresAt: number,
    userData: Record<string, unknown>
  ): void {
    this._oidc
      .getConfiguration()
      .pipe(
        take(1),
        switchMap((config) => {
          this._configId = config.configId ?? '';
          this._storage.setTokens(this._configId, {
            accessToken: message.accessToken,
            idToken: message.idToken,
            expiresAt,
            userData,
          });
          this._tokens.next(message.accessToken);

          // Requests read the token from storage, so a newer one needs nothing else.
          if (this.status() === 'signedIn') {
            return EMPTY;
          }

          return this._oidc.checkAuth().pipe(
            take(1),
            switchMap(({ isAuthenticated }) => {
              if (isAuthenticated) {
                this._oauth.listenForUserData(this._configId);
                this.status.set('signedIn');
              }
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  }

  /** Asks the parent for a token after the delay, then again until one arrives. */
  private _requestTokenIn(delay: number): void {
    this._clearTimer();
    this._timer = setTimeout(
      () => {
        this._post({ type: EmbedMessageType.TokenRequest });
        this._requestTokenIn(EMBED_TOKEN_RETRY_MS);
      },
      Math.max(delay, 0)
    );
  }

  private _clearTimer(): void {
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  /** Only to the allowed parent, though nothing secret is sent. */
  private _post(message: { type: string }): void {
    this._parent.frame.postMessage(message, this._parentOrigin);
  }
}

/** When the token expires, in milliseconds. */
function expiryOf(token: string): number | null {
  const exp = jwtClaims(token)?.['exp'];
  return typeof exp === 'number' ? exp * 1000 : null;
}
